import { DirectionalHint, Stack, Text, TooltipHost } from '@fluentui/react';
import React from 'react';
import PresencePill from './PresencePill';
import { useWindowWidth } from 'shared-state/display-options/hooks';
import { showTooltipForTruncatedText, truncateText } from 'kits/truncateTextKit';
import { useParams } from 'react-router';

// Generic tabs list renderer
export type TabItem = {
    key: string | number;
    title: string;
    usersAndVisitorsCount: {
        users: number;
        visitors: number;
    };
    groupLevel?: string;
    parentAreaId?: string;
};

export interface ITabsListProps {
    items: TabItem[];
    color?: string;
    getPresenceTooltip: (u: number, v: number) => string;
    handleAreaGroupClick: (e: React.MouseEvent<HTMLDivElement>, item: TabItem) => void;
    handleAreaClick: (e: React.MouseEvent<HTMLDivElement>, item: TabItem) => void;
}

export const getDisplayTitle = (item: TabItem): { truncatedTitle: string; tooltipContent: string } => {
    const hasPresence = item.usersAndVisitorsCount.users > 0 || item.usersAndVisitorsCount.visitors > 0;
    const truncatedTitle = hasPresence ? truncateText(item.title, 10) : truncateText(item.title, 24);
    const tooltipContent = showTooltipForTruncatedText(item.title, truncatedTitle);
    return { truncatedTitle, tooltipContent };
};

const TabsList: React.FC<ITabsListProps> = ({ items, color, getPresenceTooltip, handleAreaGroupClick, handleAreaClick }) => {
    const { areaId: areaIdParam } = useParams<{ areaId?: string }>();
    const { largeLaptop, mediumDevice } = useWindowWidth();

    if (!largeLaptop || !items?.length) return null;

    return (
        <Stack horizontal style={{ marginLeft: 30 }}>
            {items.map((item) => {
                const isSelected = item.key === areaIdParam || false;
                return (
                    <React.Fragment key={item.key}>
                        <div style={{ margin: '16px 8px', background: color ? color : 'rgba(255,255,255,0.3)', width: 1 }} />
                        {!mediumDevice && (
                            <TooltipHost content={getDisplayTitle(item).tooltipContent} directionalHint={DirectionalHint.bottomCenter}>
                                {' '}
                                <Stack
                                    horizontal
                                    styles={{
                                        root: {
                                            width: 'auto',
                                            maxWidth: 240,
                                            height: '100%',
                                            padding: '8px 16px',
                                            cursor: 'pointer',
                                            backgroundColor: isSelected
                                                ? color && color === '#fff'
                                                    ? 'rgba(255,255,255,0.3)'
                                                    : 'rgba(0,0,0,0.3)'
                                                : 'transparent',
                                            '&:hover': {
                                                backgroundColor: color && color === '#fff' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'
                                            }
                                        }
                                    }}
                                    verticalAlign="center"
                                    onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                                        if (item.groupLevel) {
                                            handleAreaGroupClick(e, item);
                                        } else {
                                            handleAreaClick(e, item);
                                        }
                                    }}
                                >
                                    <Stack verticalAlign="center" horizontal style={{ height: '100%', width: '100%' }}>
                                        <Text variant="medium" style={{ color: color || 'white', marginRight: 5 }}>
                                            {getDisplayTitle(item).truncatedTitle}
                                        </Text>

                                        <PresencePill
                                            usersAndVisitorsCount={item.usersAndVisitorsCount}
                                            color={color}
                                            getPresenceTooltip={getPresenceTooltip}
                                        />
                                    </Stack>
                                </Stack>
                            </TooltipHost>
                        )}
                    </React.Fragment>
                );
            })}
        </Stack>
    );
};

export default TabsList;
